import { API_URL } from "@/components/config/config";
import fetchWithAuth from "@/hooks/fetchWithAuth";
import { t as translate } from "@/lib/i18n";
import type { CopilotDirective } from "@/components/copilot/targets";

export type CopilotRunStatus = "pending" | "running" | "done" | "error";
export type CopilotProposalStatus =
  | ""
  | "pending"
  | "confirmed"
  | "declined"
  | "expired";

export interface CopilotStep {
  tool?: string;
  flow?: string;
  step?: string;
  label?: string;
  status?: "done" | "error" | string;
  directive?: CopilotDirective;
}

export interface CopilotProposal {
  summary?: string;
  // "update_call_submittal" and "apply_call_fields" save to a call: the preview shows what changes.
  type?: string;
  preview?: string;
  call_id?: number | string;
  [key: string]: unknown;
}

export interface CopilotMessage {
  id: number | string;
  role: "user" | "assistant" | "system";
  content: string;
  page: string;
  steps: CopilotStep[];
  proposal: CopilotProposal;
  proposal_status: CopilotProposalStatus;
  run_status: CopilotRunStatus;
  error: string | null;
  created_at: string;
  updated_at: string;
  stale: boolean;
}

export interface CopilotThread {
  id: number;
  created_at: string;
  updated_at: string;
  messages: CopilotMessage[];
}

export interface CopilotGreeting {
  greeting: string;
  offers: Array<{
    label: string;
    prompt: string;
  }>;
  // A walkthrough film to show under the greeting; null once it is no longer needed.
  film?: "schedule-interview" | null;
}

export class CopilotApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "CopilotApiError";
    this.status = status;
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  let body: { data?: T; error?: string; detail?: string } | null = null;
  try {
    body = await response.json();
  } catch {
    // The response status is still useful when the backend returns no JSON.
  }

  if (!response.ok) {
    throw new CopilotApiError(
      body?.error || body?.detail || response.statusText || translate("Request failed"),
      response.status
    );
  }
  return body?.data as T;
}

export async function getCopilotThread(): Promise<CopilotThread> {
  const response = await fetchWithAuth(`${API_URL}/copilot/thread/`);
  return parseResponse<CopilotThread>(response);
}

export async function getCopilotGreeting(): Promise<CopilotGreeting> {
  const response = await fetchWithAuth(`${API_URL}/copilot/greeting/`);
  return parseResponse<CopilotGreeting>(response);
}

export async function postCopilotMessage(
  text: string,
  page: string
): Promise<{ thread_id: number; message_id: number }> {
  const response = await fetchWithAuth(`${API_URL}/copilot/messages/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text, page }),
  });
  return parseResponse<{ thread_id: number; message_id: number }>(response);
}

export async function postCopilotArrival(
  page: string,
  target?: string
): Promise<boolean> {
  const response = await fetchWithAuth(`${API_URL}/copilot/arrived/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(target === undefined ? { page } : { page, target }),
  });
  if (response.status === 204) return false;
  await parseResponse<{ thread_id: number; message_id: number }>(response);
  return response.status === 202;
}

export async function updateCopilotProposal(
  messageId: number | string,
  action: "confirm" | "decline"
): Promise<{ ok: boolean; error?: string; status?: string }> {
  const response = await fetchWithAuth(
    `${API_URL}/copilot/messages/${messageId}/proposal/`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action }),
    }
  );
  return parseResponse<{ ok: boolean; error?: string; status?: string }>(response);
}

// The onboarding films this recruiter has watched to the end, kept on the profile so
// progress follows them across devices.
export async function getOnboardingFilms(): Promise<string[]> {
  const response = await fetchWithAuth(`${API_URL}/onboarding/films/`);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new CopilotApiError(body?.error || translate("Request failed"), response.status);
  return Array.isArray(body?.watched) ? body.watched : [];
}

export type HiringPersona = "clients" | "own_company" | "skipped";

// The one optional sign-up question, "Who are you hiring for?". Asked once: a
// skip is stored as well, so persona is null only for a recruiter not yet asked.
export async function getHiringPersona(): Promise<HiringPersona | null> {
  const response = await fetchWithAuth(`${API_URL}/onboarding/persona/`);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new CopilotApiError(body?.error || translate("Request failed"), response.status);
  return body?.persona ?? null;
}

export async function saveHiringPersona(persona: HiringPersona): Promise<HiringPersona> {
  const response = await fetchWithAuth(`${API_URL}/onboarding/persona/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ persona }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new CopilotApiError(body?.error || translate("Request failed"), response.status);
  return body?.persona ?? persona;
}

export async function markOnboardingFilm(film: string): Promise<string[]> {
  const response = await fetchWithAuth(`${API_URL}/onboarding/films/`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ film }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new CopilotApiError(body?.error || translate("Request failed"), response.status);
  return Array.isArray(body?.watched) ? body.watched : [];
}
